/**
 * Conditional Node Configuration - edit a single if/else condition
 */

import React, { useMemo, useState } from 'react';
import type { PipelineNode, PipelineDefinition, StepCondition, ConditionOperator } from '../types/pipeline';
import { getNodeOutputSchema, getScalarFields } from '../utils/nodeOutputSchema';
import { operatorsForFieldType } from '../utils/conditionOperators';
import { pathType } from '../utils/outputPath';
import { OutputPathPicker, useAgentOutputSchema } from './OutputPathPicker';
import './ConditionalNodeConfig.css';

interface ConditionalNodeConfigProps {
  node: PipelineNode;
  pipeline: PipelineDefinition | null;
  onSave: (condition: StepCondition) => void;
  onClose: () => void;
}

export const ConditionalNodeConfig: React.FC<ConditionalNodeConfigProps> = ({
  node,
  pipeline,
  onSave,
  onClose,
}) => {
  const existing = node.config?.condition as StepCondition | undefined;
  const pipelineNodes = pipeline?.nodes ?? [];
  const pipelineEdges = pipeline?.edges ?? [];

  // Default the upstream step to whatever's connected via the single
  // incoming edge - the common case - but leave it editable.
  const incomingEdge = useMemo(
    () => pipelineEdges.find(e => e.target === node.id),
    [pipelineEdges, node.id]
  );

  const [upstreamNodeId, setUpstreamNodeId] = useState<string>(
    existing?.upstream_step_reference ?? incomingEdge?.source ?? ''
  );
  const [field, setField] = useState(existing?.field ?? '');
  const [operator, setOperator] = useState<ConditionOperator>(existing?.operator ?? 'equals');
  const [value, setValue] = useState<string>(existing?.value !== undefined ? String(existing.value) : '');

  const upstreamNode = pipelineNodes.find(n => n.id === upstreamNodeId) || null;

  // Agent nodes describe their output with the agent's JSON output_schema; built-in
  // task types fall back to the static per-task-type field list.
  const { schema: agentSchema, loading: agentSchemaLoading } = useAgentOutputSchema(upstreamNode?.agent_id);
  const scalarFields = useMemo(
    () => (upstreamNode ? getScalarFields(getNodeOutputSchema(upstreamNode.task_type)) : []),
    [upstreamNode]
  );

  const fieldType = agentSchema ? pathType(field, agentSchema) : scalarFields.find(f => f.path === field)?.type;
  const operatorOptions = operatorsForFieldType(fieldType === 'unknown' ? undefined : fieldType);

  const canSave = Boolean(upstreamNodeId && field && operator);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      upstream_step_reference: upstreamNodeId,
      field,
      operator,
      value: operator === 'exists' ? undefined : value,
    });
    onClose();
  };

  return (
    <div className="conditional-node-config">
      <div className="config-header">
        <div>
          <h3>{node.label || 'Branch'}</h3>
          <p className="section-description">Branch condition (if / else)</p>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="config-body">
        <div className="config-section">
          <div className="condition-row">
            <label>Upstream Step</label>
            <select
              className="condition-input"
              value={upstreamNodeId}
              onChange={(e) => {
                setUpstreamNodeId(e.target.value);
                setField('');
              }}
            >
              <option value="">Select a step...</option>
              {pipelineNodes
                .filter(n => n.id !== node.id)
                .map(n => (
                  <option key={n.id} value={n.id}>
                    {n.label || n.id}
                  </option>
                ))}
            </select>
          </div>

          <div className="condition-row">
            <label>Field</label>
            {upstreamNode?.agent_id ? (
              <OutputPathPicker
                value={field}
                onChange={setField}
                schema={agentSchema}
                loading={agentSchemaLoading}
                placeholder="e.g., issues.0.severity"
              />
            ) : scalarFields.length > 0 ? (
              <select className="condition-input" value={field} onChange={(e) => setField(e.target.value)}>
                <option value="">Select a field...</option>
                {scalarFields.map(f => (
                  <option key={f.path} value={f.path}>
                    {f.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="condition-input"
                value={field}
                onChange={(e) => setField(e.target.value)}
                placeholder="e.g., status"
              />
            )}
          </div>

          <div className="condition-row">
            <label>Operator</label>
            <select
              className="condition-input"
              value={operator}
              onChange={(e) => setOperator(e.target.value as ConditionOperator)}
            >
              {operatorOptions.map(op => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          {operator !== 'exists' && (
            <div className="condition-row">
              <label>Value</label>
              <input
                type="text"
                className="condition-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Comparison value"
              />
            </div>
          )}
        </div>

        <div className="config-help">
          <h4>Routing</h4>
          <ul>
            <li>
              Drag connections from the node's <strong>true</strong> and <strong>false</strong> handles on the
              canvas to route downstream steps for each outcome.
            </li>
          </ul>
        </div>
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};
